import { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import usePaymentMethod from '../../../../purchasebills/hooks/usePaymentMethod';
import { getCustomerList } from '../../../../../services/customers';
import InputSelect from '../../../../../component/inputSelect/InputSelect';
import InputField from '../../../../../component/form/FormInput';
import { getCurrentDate, getDate } from '../../../../../utils/dateUtils';
import tooltipIcon from '../../../../../assets/image/tooltip_vendor name.png';
import CustomDrawer from '../../../components/customDrawer';
import AddCustomerModel from '../../invoice/components/addCustomerModel';
import AddPaymentMethodModal from '../../../../purchasebills/components/AddPaymentMethodModal';
import Button from '../../../../../component/ui/Button';
import Modal from '../../../../../component/modal/Modal';
import {
	fetchSalesReturnDetails,
	fetchSalesReturnNumber,
} from '../../../../../services/sales_return';
import { format, parseISO } from 'date-fns';
import { totalAmountWithoutTax } from '../../../components/TaxesCalculation';
import SalesRetuenProductTableHeader from './salesReturnProductaTable/salesReturnProductTableHeader';
import InvoiceFooter from '../../invoice/createInvoice/InvoiceFooter';

const SalesReturnForm = ({
	setError,
	error,
	tableError,
	setTableError,
	productTable,
	salesReturn,
	setSalesReturn,
	setProductTable,
	setAdditionDiscountAndTax,
	additionDiscountAndTax,
	totalDiscountAmount,
	totalTaxesAmount,
	totalAmount,
	calculateAmount,
}) => {
	const location = useLocation();
	const param = useParams();
	const id = param?.id;
	const [customerList, setCustomerList] = useState([]);
	const { paymentMethodList, setSearchPaymentMethod } = usePaymentMethod();
	const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false);
	const [isAddPaymentMethod, setIsAddPaymentMethod] = useState(false);
	const [isPaymentMethodFocus, setIsPaymentMethodFocus] = useState(false);
	const [searchCustomer, setSearchCustomer] = useState(false);
	const [isOpenPastDate, setIsOpenPastDate] = useState(false);
	const [isUsedPastDate, setIsUsedPastDate] = useState(false);
	const [isDateFocus, setIsDateFocus] = useState(false);
	const [drawerType, setDrawerType] = useState('');
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [autofocus, setAutofocus] = useState(false);
	const [params, setParams] = useState({
		customer: {
			status: 1,
			page: 1,
			page_size: 10000,
			dropdown: true,
			order_by: null,
			sort_by: null,
			filter_by: null,
			filter_value: null,
		},
	});
	const handleFormData = (type, value) => {
		setSalesReturn((prev) => ({
			...prev,
			[type]: value,
		}));

		let newError = { ...error };
		if (type === 'customer_id') {
			if (value === '' || value === null || value === undefined) {
				newError[type] = 'Please select valid  customer name from the list';
			} else {
				delete newError[type];
			}
		}

		if (type === 'payment_method') {
			if (value === '' || value === null || value === undefined) {
				newError[type] = 'Please select valid  payment method from the list';
			} else {
				delete newError[type];
			}
		}

		setError(newError);
	};

	const confirmUsedPastDate = () => {
		const newError = { ...error };
		delete newError.date;
		setError(newError);
		setIsUsedPastDate(true);
		setIsOpenPastDate(false);
		setIsPaymentMethodFocus(true);
	};

	const cancelUsedPastDate = () => {
		setSalesReturn((prev) => ({
			...prev,
			date: getCurrentDate(),
		}));
		setIsOpenPastDate(false);
		setIsDateFocus(true);
	};

	const handleFetchCustomerList = useCallback(async () => {
		try {
			const { customers } = await getCustomerList(params?.customer);
			const customerOptionList = customers?.map((customer) => {
				return {
					key: customer?.id,
					value: customer?.name,
				};
			});
			setCustomerList(customerOptionList);
		} catch (error) {
			toast.error(error.message ?? 'Something went wrong!');
		}
	}, [params?.customer]);

	useEffect(() => {
		let timer = setTimeout(() => {
			setParams((prev) => ({
				...prev,
				customer: {
					...prev.customer,
					filter_by: 'name',
					filter_value: searchCustomer,
					status: 1,
				},
			}));
		}, 500);
		return () => clearTimeout(timer);
	}, [searchCustomer]);

	useEffect(() => {
		if (!salesReturn.customer_id) {
			handleFetchCustomerList();
		}
	}, [params.customer, id, salesReturn.customer_id]);

	async function getSalesReturnDetails() {
		try {
			const { sales_return } = await fetchSalesReturnDetails({ ids: [id] });
			const {
				return_number,
				reference,
				date,
				discount,
				adjustment,
				round_off,
				notes,
				customer,
				sub_total,
				payment_mode,
				sales_return_invoices,
			} = sales_return;

			setSalesReturn((prev) => ({
				...prev,
				customer_id: customer?.id,
				sales_return_number: return_number,
				reference: reference,
				date: format(parseISO(date), 'yyyy-MM-dd'),
				payment_method: payment_mode?.payment_method_id,
				notes: notes,
			}));
			var InvoiceProductList = generateSalesReturnInvoice(sales_return_invoices);
			setProductTable(InvoiceProductList);
			setSearchCustomer(customer?.name);
			setAdditionDiscountAndTax((prev) => ({
				...prev,
				discount: Number((Number(discount) * 100) / Number(sub_total)).toFixed(2),
				adj: Number(adjustment ?? 0),
				round_off: Number(round_off ?? 0),
			}));
		} catch (error) {
			toast.error(error.message || 'Something went wrong!');
		}
	}

	function generateSalesReturnInvoice(srNvoice) {
		const sr_invoice = srNvoice.reduce((acc, sr) => {
			acc.push({
				return_type_id: sr?.return_type?.id || '',
				invoice_id: sr?.invoice_id,
				product_id: sr?.product_id,
				product_name: sr?.product?.product_name,
				batch_no: sr?.product_batch?.batch_no,
				product_batch_id: sr?.product_batch_id,
				quantity: sr?.qty !== null && sr?.qty !== undefined ? sr?.qty : sr?.quantity,
				expiry_date:
					sr?.exp !== null && sr?.exp !== undefined
						? getDate(sr?.exp, true, true, true)
						: getDate(sr?.expiry, true, true, true),
				mrp: sr?.mrp,
				amount: sr?.amount,
				product_tax1: Number(sr?.product?.tax1),
				product_tax2: Number(sr?.product?.tax2),
			});
			return acc;
		}, []);
		return sr_invoice;
	}

	async function handleFetchSalesReturnNumber() {
		try {
			const sr = await fetchSalesReturnNumber();
			handleFormData('sales_return_number', sr?.number);
		} catch (error) {
			toast.error(error.message ?? 'Something went wrong!');
		}
	}
	useEffect(() => {
		if (!id) {
			handleFetchSalesReturnNumber();
		} else {
			getSalesReturnDetails();
		}
	}, [id]);

	function setCustomerData() {
		if (location && location?.state.key) {
			const state = location?.state;
			setCustomerList([state]);
			setSalesReturn((prev) => ({
				...prev,
				customer_id: state?.key,
			}));
		}
	}

	useEffect(() => {
		if (location && location?.state && location.state.key) {
			setCustomerData();
		} else if (location && location?.state && location.state.invoice) {
			setSalesReturnData();
		}
	}, [location]);

	useEffect(() => {
		if (!autofocus) {
			setAutofocus(true);
		}
	}, [autofocus]);

	function setSalesReturnData() {
		if (location && location?.state && location.state.invoice) {
			const { customer, invoice_products } = location.state.invoice;
			setSalesReturn((prev) => ({
				...prev,
				customer_id: customer?.id,
			}));
			var InvoiceProductList = generateSalesReturnInvoice(invoice_products);
			setProductTable(InvoiceProductList);
			setSearchCustomer(customer?.name);
		}
	}

	return (
		<div>
			<div className="max-w-1297 px-3 py-3">
				<div className="row">
					<div className="col-xl-4 col-6">
						<InputSelect
							options={customerList}
							label="Customer Name"
							placeholder="Customer Name"
							autoComplete="off"
							required={true}
							returnInputValue={false}
							value={salesReturn?.customer_id}
							onSelect={(option) => {
								handleFormData('customer_id', option.key);
								setProductTable([]);
							}}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('customer_id', value);
								}
								setSearchCustomer(value);
								setProductTable([]);
							}}
							error={error?.customer_id}
							autoFocus={autofocus}
							leftIcons={
								salesReturn?.customer_id
									? [
										{
											src: tooltipIcon,
											alt: 'Show Customer Details',
											onClick: () => {
												setDrawerType('customer');
												setIsDrawerOpen(true);
											},
										},
									]
									: []
							}
							onAddNew={() => {
								setIsAddCustomerModalOpen(true);
							}}
						/>
					</div>

					<div className="col-xl-2 col-6">
						<InputField
							label="Sales Return #"
							placeholder="SR-001"
							type="text"
							autoComplete="off"
							required={true}
							disabled={true}
							error={error?.sales_return_number}
							value={salesReturn.sales_return_number}
							onChange={(value) => {
								handleFormData('sales_return_number', value);
							}}
						/>
					</div>
					<div className="col-xl-2 col-4">
						<InputField
							label="Reference #"
							placeholder="INV-0001"
							type="text"
							autoComplete="off"
							required={false}
							value={salesReturn.reference}
							onChange={(value) => {
								handleFormData('reference', value);
							}}
						/>
					</div>
					<div className="col-xl-2 col-4">
						<InputField
							label="Sales Return Date"
							placeholder="Enter Date"
							type="date"
							autoComplete="off"
							required={true}
							error={error?.date}
							autoFocus={isDateFocus}
							value={salesReturn.date}
							onBlur={(e) => {
								var value = e.target.value;
								const newError = { ...error };
								var date = new Date(value);
								var currentDate = new Date();
								currentDate.setDate(currentDate.getDate() + 1);
								let year = date.getFullYear().toString();
								if (year.length === 4 && new Date(value) < new Date().setHours(0, 0, 0, 0)) {
									if (!isUsedPastDate) {
										setIsOpenPastDate(true);
									}
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
								} else if (
									year.length === 4 &&
									new Date(value) > currentDate.setHours(0, 0, 0, 0)
								) {
									setIsOpenPastDate(false);
									newError.date = 'Date cannot be in the future';
									setError(newError);
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
								} else {
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
									setIsOpenPastDate(false);
									delete newError.date;
									setError(newError);
								}
							}}
							onChange={(value) => {
								const newError = { ...error };
								var date = new Date(value);
								var currentDate = new Date();
								currentDate.setDate(currentDate.getDate() + 1);
								let year = date.getFullYear().toString();
								if (year.length === 4 && new Date(value) < new Date().setHours(0, 0, 0, 0)) {
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
								} else if (
									year.length === 4 &&
									new Date(value) > currentDate.setHours(0, 0, 0, 0)
								) {
									setIsOpenPastDate(false);
									newError.date = 'Date cannot be in the future';
									setError(newError);
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
								} else {
									setSalesReturn((prev) => ({
										...prev,
										date: value,
									}));
									setIsOpenPastDate(false);
									delete newError.date;
									setError(newError);
								}
							}}
							max={getCurrentDate()}
						/>
					</div>
					<div className="col-xl-2 col-4">
						<InputSelect
							options={paymentMethodList}
							label="Refund Mode"
							placeholder="Payment Mode"
							required={true}
							autoComplete="off"
							autoFocus={isPaymentMethodFocus}
							value={salesReturn.payment_method}
							onChange={(value) => {
								if (value === '' || value === null) {
									handleFormData('payment_method', value);
								}
								setSearchPaymentMethod(value);
							}}
							onSelect={(options) => {
								handleFormData('payment_method', options?.key);
							}}
							error={error?.payment_method}
							onAddNew={() => {
								setIsAddPaymentMethod(true);
								setIsPaymentMethodFocus(false);
							}}
						/>
					</div>
				</div>
				<div className="row">
					<div
						className="col"
						style={{ marginBottom: '10px' }}
					>
						<InputField
							label="Notes"
							placeholder=""
							type="text"
							required={false}
							autoComplete="off"
							value={salesReturn.motes}
							onChange={(value) => {
								handleFormData('notes', value);
							}}
						/>
					</div>
				</div>
				<div className="overflow-hidden pb-xl-0 pb-5">
					<SalesRetuenProductTableHeader
						tableError={tableError}
						customerId={salesReturn?.customer_id}
						setTableError={setTableError}
						productTable={productTable}
						setProductTable={setProductTable}
					/>
				</div>
			</div>
			<InvoiceFooter
				type="sales_return"
				additionDiscountAndTax={additionDiscountAndTax}
				setAdditionDiscountAndTax={setAdditionDiscountAndTax}
				totalDiscountAmount={totalDiscountAmount}
				totalTaxesAmount={totalTaxesAmount}
				totalAmount={totalAmount}
				calculateAmount={calculateAmount}
				invoiceTable={productTable}
			/>

			{isOpenPastDate && (
				<Modal
					className="delivery-modal"
					title="Confirm Past Date"
					isOpen={isOpenPastDate}
					onClose={() => cancelUsedPastDate()}
					cancelButton={true}
				>
					<p>You are creating a transaction in the past date. Do you want to continue?</p>

					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'flex-start',
							marginTop: '20px',
						}}
					>
						<Button
							button="Proceed"
							type="button"
							className="btn btn-primary"
							onClick={confirmUsedPastDate}
						/>
						<span style={{ width: '20px' }}></span>
						<Button
							button="Cancel"
							type="button"
							className="btn btn-danger"
							onClick={() => cancelUsedPastDate()}
						/>
					</div>
				</Modal>
			)}

			{isAddCustomerModalOpen && (
				<AddCustomerModel
					setIsAddCustomerModalOpen={setIsAddCustomerModalOpen}
					isAddCustomerModalOpen={isAddCustomerModalOpen}
					setInvoice={setSalesReturn}
					setCustomerList={setCustomerList}
				/>
			)}

			{isAddPaymentMethod && (
				<AddPaymentMethodModal
					type={'sales_return'}
					paymentMethodList={paymentMethodList}
					isAddPaymentMethod={isAddPaymentMethod}
					setIsAddPaymentMethod={setIsAddPaymentMethod}
					setIsPaymentMethodFocus={setIsPaymentMethodFocus}
					setPurchaseBillFormData={setSalesReturn}
				/>
			)}
			{
				<CustomDrawer
					isOpen={isDrawerOpen}
					onClose={() => setIsDrawerOpen(false)}
					id={drawerType === 'customer' ? salesReturn.customer_id : null}
					drawerType={drawerType}
					activeDrawer={'Details'}
				/>
			}
		</div>
	);
};
export default SalesReturnForm;
